import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Megaphone, Plus } from 'lucide-react'
import { AuthService } from '@/services/core/auth'
import { PoliticalCampaignsApi } from '@/services/political/politicalCampaignsApi'
import { PoliticalCampaign } from '../../types/models'
import { apiErrorDescription } from '@/services/core/apiError'
import { AppShell } from '../../components/AppShell'
import { CampaignTable } from '../../components/CampaignTable'
import { BulkActionBar } from '../../components/BulkActionBar'
import { StatusFilterButton } from '../../components/StatusFilter'
import { ListCard } from '../../components/ListCard'
import { ListCardHeader } from '../../components/ListCardHeader'
import { SearchInput } from '../../components/SearchInput'
import { CardContent, CardFooter, CardIcon } from '@/components/ui/card'
import { usePersistedState } from '@/hooks/usePersistedState'
import { useUrlSearch } from '@/hooks/useUrlSearch'

export function PoliticalCampaignsPage() {
  const navigate = useNavigate()
  const hasToken = AuthService.isAuthenticated()
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [search, setSearch] = useUrlSearch()
  const {
    data: campaigns = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['politicalCampaigns', search],
    queryFn: () => PoliticalCampaignsApi.getAll(search),
    enabled: hasToken,
  })

  const [statusFilter, setStatusFilter] = usePersistedState<'all' | 'ACTIVE' | 'INACTIVE'>('filters.politicalCampaigns.status', 'ACTIVE')
  const [selection, setSelection] = useState<PoliticalCampaign[]>([])
  const [bulkLoading, setBulkLoading] = useState(false)

  const filtered = statusFilter === 'all' ? campaigns : campaigns.filter((c) => c.objectState === statusFilter)

  const bulkSetStatus = async (state: 'ACTIVE' | 'INACTIVE') => {
    setBulkLoading(true)
    try {
      await Promise.all(
        selection.map((c) =>
          PoliticalCampaignsApi.update(c.id, {
            name: c.name,
            description: c.description,
            politicalEntityId: c.politicalEntityId,
            electionId: c.electionId,
            totalBudget: c.totalBudget,
            currency: c.currency,
            topics: c.topics,
            objectState: state,
          })
        )
      )
      queryClient.invalidateQueries({ queryKey: ['politicalCampaigns'] })
      toast.success(t('common.done'), {
        description: t(state === 'ACTIVE' ? 'political.doneDetail_activate' : 'political.doneDetail_deactivate', { count: selection.length }),
        duration: 3000,
      })
      setSelection([])
    } catch (e) {
      toast.error(t('common.error'), { description: apiErrorDescription(e), duration: 5000 })
    } finally {
      setBulkLoading(false)
    }
  }

  return (
    <AppShell crumbs={[{ label: t('political.campaigns') }]}>
      <div className="ad-page">
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <h1>{t('political.campaigns')}</h1>
            <p>{t('political.campaignsSubtitle')}</p>
          </div>
          <div className="ad-page__actions">
            <Button
              onClick={() => navigate(`/political/campaigns/${crypto.randomUUID().replace(/-/g, '').slice(0, 12)}?new=true`)}
            ><Plus className="size-4" /> {t('political.newCampaign')}</Button>
          </div>
        </div>

        {error && (
          <div className="auth-form__error" style={{ marginBottom: 16 }}>
            <span className="pi pi-exclamation-circle" />
            {(error as Error).message}
          </div>
        )}

        <ListCard>
          <ListCardHeader
            title={
              <>
                <CardIcon icon={Megaphone} />
                {t('political.campaigns')}
              </>
            }
            count={filtered.length}
            subtitle={t('political.clickToEdit')}
            controls={
              <>
                <SearchInput value={search} onChange={(v) => { setSearch(v); setSelection([]) }} />
                <StatusFilterButton value={statusFilter} onChange={setStatusFilter} />
              </>
            }
          />

          <CardContent className="p-0">
            <BulkActionBar
              count={selection.length}
              onClear={() => setSelection([])}
              loading={bulkLoading}
              onActivate={() => bulkSetStatus('ACTIVE')}
              onDeactivate={() => bulkSetStatus('INACTIVE')}
            />
            <CampaignTable
              campaigns={filtered}
              loading={isLoading}
              emptyMessage={
                hasToken ? t('political.noCampaignsFound') : t('common.signInToLoad')
              }
              selection={selection}
              onSelectionChange={setSelection}
            />
          </CardContent>

          {filtered.length > 0 && (
            <CardFooter>
              <span style={{ fontSize: 12, color: 'var(--ad-text-muted)' }}>
                {t('political.campaignCount', { count: filtered.length })}
              </span>
            </CardFooter>
          )}
        </ListCard>
      </div>
    </AppShell>
  )
}
